import React from 'react';
import {
  ShieldCheck, FileText, Calendar, Users, AlertCircle, CheckCircle2, XCircle,
  Mail, Phone, Clock, TrendingUp, TrendingDown, Minus, Activity, Heart,
  MessageSquare, Award, Star, Zap, AlertOctagon,
} from 'lucide-react';

export const fmt = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
export const fmtPct = (v) => v == null ? '—' : `${Math.round(v)}%`;
export const fmtCompact = (v) => v == null ? '—' : Math.abs(v) >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : Math.abs(v) >= 1_000 ? `$${(v / 1_000).toFixed(0)}K` : `$${Math.round(v)}`;

// ── Rating styling ──────────────────────────────────────────────────────────
export const RATING_STYLE = {
  outstanding: { label: 'Outstanding',              bg: 'bg-amber-50',    text: 'text-amber-800',    border: 'border-amber-300',    dot: 'bg-amber-500',    icon: Star },
  exceeds:     { label: 'Exceeds Expectation',      bg: 'bg-emerald-50',  text: 'text-emerald-800',  border: 'border-emerald-300',  dot: 'bg-emerald-500',  icon: TrendingUp },
  meets:       { label: 'Meets Expectation',        bg: 'bg-blue-50',     text: 'text-blue-800',     border: 'border-blue-300',     dot: 'bg-blue-500',     icon: CheckCircle2 },
  below:       { label: 'Below Expectation',        bg: 'bg-red-50',      text: 'text-red-800',      border: 'border-red-300',      dot: 'bg-red-500',      icon: AlertOctagon },
  pending:     { label: 'Awaiting Data',            bg: 'bg-[#FAFAFA]',   text: 'text-[#737373]',    border: 'border-[#E5E5E5]',    dot: 'bg-[#A3A3A3]',    icon: Activity },
};

// ── Rating pill (used everywhere ratings appear) ────────────────────────────
export const RatingPill = ({ ratingKey, size = 'sm' }) => {
  const style = RATING_STYLE[ratingKey] || RATING_STYLE.pending;
  const Icon = style.icon;
  const sizing = size === 'lg'
    ? 'px-3 py-1.5 text-[12px]'
    : size === 'xs'
      ? 'px-1.5 py-0.5 text-[10px]'
      : 'px-2 py-0.5 text-[11px]';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${style.bg} ${style.text} ${style.border} ${sizing}`}>
      <Icon className={size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} strokeWidth={2} />
      {style.label}
    </span>
  );
};

// ── Performance Rating Block (the headline section) ─────────────────────────
export const PerformanceRatingBlock = ({ performanceRating, subjectLabel = 'Manager' }) => {
  if (!performanceRating) return null;
  const { overall, pillars } = performanceRating;
  const style = RATING_STYLE[overall.ratingKey] || RATING_STYLE.pending;
  const Icon = style.icon;

  return (
    <section data-testid="performance-rating-block" className={`relative overflow-hidden rounded-[14px] border ${style.border} ${style.bg} shadow-[0_2px_10px_rgba(0,0,0,0.02)]`}>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#737373] mb-2">{subjectLabel} Performance Rating</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className={`w-12 h-12 rounded-[10px] flex items-center justify-center ${style.bg} border ${style.border}`}>
                <Icon className={`w-6 h-6 ${style.text}`} strokeWidth={1.5} />
              </div>
              <div>
                <h2 className={`text-2xl lg:text-3xl font-bold font-heading tracking-tight ${style.text}`} data-testid="performance-rating-headline">
                  {overall.rating}
                </h2>
                {overall.avgIndex != null && (
                  <p className="text-[11px] text-[#737373] mt-0.5">
                    Composite index <span className="font-mono font-semibold text-[#171717]">{overall.avgIndex.toFixed(2)}</span> · {overall.ratedPillars} of {pillars.length} pillars rated
                  </p>
                )}
                {overall.avgIndex == null && (
                  <p className="text-[11px] text-[#737373] mt-0.5">
                    Insufficient signal across the rated pillars yet — supply more data to activate the rating.
                  </p>
                )}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[#737373] max-w-md leading-relaxed">
            Blended from four pillars below. Each pillar is rated against published bands. One weak pillar pulls the overall down — missing data does not.
          </p>
        </div>

        {/* Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          {pillars.map(p => <PillarCard key={p.key} pillar={p} />)}
        </div>
      </div>
    </section>
  );
};

const PillarCard = ({ pillar }) => {
  const ratingKey = pillar.ratingKey || 'pending';
  const style = RATING_STYLE[ratingKey];
  const valueDisplay = pillar.value == null
    ? '—'
    : pillar.unit === '%'
      ? `${Math.round(pillar.value)}%`
      : pillar.unit === '/100'
        ? `${Math.round(pillar.value)}/100`
        : pillar.value > 0 ? `+${pillar.value}` : `${pillar.value}`;

  // Threshold labels for the tooltip-style description
  const [t1, t2, t3] = pillar.thresholds || [];
  return (
    <div data-testid={`pillar-${pillar.key}`} className={`bg-white border rounded-[10px] p-4 ${style.border}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#737373] leading-snug">{pillar.label}</p>
        <RatingPill ratingKey={ratingKey} size="xs" />
      </div>
      <p className="text-2xl font-bold font-heading text-[#171717] mt-1">{valueDisplay}</p>
      <p className="text-[10px] text-[#A3A3A3] mt-2 leading-relaxed">{pillar.description}</p>
      {pillar.thresholds && (
        <p className="text-[9px] text-[#A3A3A3] mt-2 font-mono">
          &lt; {t1} below · &lt; {t2} meets · ≤ {t3} exceeds · &gt; {t3} outstanding
        </p>
      )}
      {pillar.note && <p className="text-[10px] text-[#A3A3A3] italic mt-2">{pillar.note}</p>}
    </div>
  );
};

// ── KPI Card ────────────────────────────────────────────────────────────────
export const KPICard = ({ icon: Icon, label, value, sublabel, accent = 'gray', testId }) => {
  const accents = {
    gray:    'border-[#E5E5E5]',
    emerald: 'border-emerald-200 bg-emerald-50/30',
    amber:   'border-amber-200 bg-amber-50/30',
    red:     'border-red-200 bg-red-50/30',
    blue:    'border-blue-200 bg-blue-50/30',
  };
  return (
    <div data-testid={testId} className={`bg-white border rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)] ${accents[accent]}`}>
      <Icon className="w-4 h-4 text-[#A3A3A3] mb-2" strokeWidth={1.5} />
      <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-[#171717] mt-1 font-heading">{value}</p>
      {sublabel && <p className="text-[10px] text-[#737373] mt-0.5">{sublabel}</p>}
    </div>
  );
};

// ── Agreement / At-Risk Block ───────────────────────────────────────────────
export const AgreementBlock = ({ agreement }) => {
  if (!agreement) return null;
  return (
    <Section title="Management Agreement & Revenue at Risk" icon={FileText} testId="dd-agreement">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPICard icon={CheckCircle2} label="Under Contract" value={`${agreement.plansUnderContract}/${agreement.totalPlans}`} sublabel={`${agreement.pctUnderContract}% of portfolio`} accent="emerald" testId="dd-agreement-under" />
        <KPICard icon={XCircle} label="At Risk / Lapsed" value={agreement.plansAtRisk} sublabel="no future-dated expiry" accent={agreement.plansAtRisk > 0 ? 'amber' : 'gray'} testId="dd-agreement-risk" />
        <KPICard icon={TrendingUp} label="Revenue Under Contract" value={fmtCompact(agreement.revenueUnderContract)} accent="emerald" testId="dd-revenue-under" />
        <KPICard icon={AlertCircle} label="Revenue At Risk" value={fmtCompact(agreement.revenueAtRisk)} accent={agreement.revenueAtRisk > 0 ? 'red' : 'gray'} testId="dd-revenue-risk" />
      </div>
      {agreement.atRiskPlans.length > 0 && (
        <div className="rounded-[8px] border border-[#F0F0F0] bg-[#FAFAFA] overflow-hidden">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#737373] px-3 py-2 bg-white border-b border-[#F0F0F0]">Top plans at risk (no current agreement)</p>
          <table className="w-full">
            <tbody>
              {agreement.atRiskPlans.map(p => (
                <tr key={p.planNumber} className="border-b border-[#F0F0F0] last:border-0">
                  <td className="px-3 py-2 text-[12px] font-mono text-[#171717]">{p.planNumber}</td>
                  <td className="px-3 py-2 text-[12px] text-[#525252] truncate max-w-[280px]">{p.propertyName || '—'}</td>
                  <td className="px-3 py-2 text-[12px] text-right font-semibold text-[#171717]">{fmtCompact(p.revenue)}</td>
                  <td className="px-3 py-2 text-[11px] text-right text-[#A3A3A3]">{p.contractExpiry || 'no expiry on file'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

// ── Compliance Element Block ────────────────────────────────────────────────
export const ComplianceBlock = ({ compliance }) => {
  if (!compliance) return null;
  const els = Object.entries(compliance);
  return (
    <Section title="Compliance — Core Elements Only" icon={ShieldCheck} subtitle="Insurance, Financials, AGM and Committee Meetings — the categories that drive committee risk." testId="dd-compliance">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {els.map(([key, c]) => (
          <ComplianceElementCard key={key} element={c} testId={`dd-compliance-${key}`} />
        ))}
      </div>
    </Section>
  );
};

const ComplianceElementCard = ({ element, testId }) => {
  const { label, pct, compliant, total, dataAvailable } = element;
  if (!dataAvailable) {
    return (
      <div data-testid={testId} className="bg-white border border-dashed border-[#E5E5E5] rounded-[10px] p-4 text-center flex flex-col justify-center min-h-[120px]">
        <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">{label}</p>
        <p className="text-[11px] text-[#A3A3A3] mt-2 leading-snug">Not in the current<br/>upload. Re-upload<br/>PlanStatus.xlsx to populate.</p>
      </div>
    );
  }
  let accent = 'emerald';
  if (pct < 60) accent = 'red';
  else if (pct < 85) accent = 'amber';
  const accentCls = { emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200', amber: 'text-amber-700 bg-amber-50 border-amber-200', red: 'text-red-700 bg-red-50 border-red-200' }[accent];
  return (
    <div data-testid={testId} className={`bg-white border rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)] ${accentCls.split(' ').pop()}`}>
      <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-[#171717] mt-1 font-heading">{fmtPct(pct)}</p>
      <p className="text-[10px] text-[#737373] mt-0.5">{compliant} of {total} compliant</p>
      <div className="mt-3 h-1.5 rounded-full bg-[#F0F0F0] overflow-hidden">
        <div className={`h-full ${accent === 'emerald' ? 'bg-emerald-500' : accent === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct || 0}%` }} />
      </div>
    </div>
  );
};

// ── Activity Block (with placeholders) ──────────────────────────────────────
export const ActivityBlock = ({ activity }) => {
  if (!activity) return null;
  const handleMin = activity.handleTimeAvgSeconds ? Math.round(activity.handleTimeAvgSeconds / 60 * 10) / 10 : null;
  const talkMin = activity.callsTalkTimeSeconds != null ? Math.round(activity.callsTalkTimeSeconds / 60) : null;
  const hasCalls = activity.callsDataAvailable;
  return (
    <Section title="Activity Load" icon={Activity} subtitle="Communication and meeting throughput across the portfolio. Phone calls are a key component of workload." testId="dd-activity">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KPICard icon={MessageSquare} label="Emails Received" value={activity.messagesReceived.toLocaleString()} testId="dd-activity-msgs" />
        <KPICard icon={Activity} label="New Conversations" value={activity.newConversations.toLocaleString()} testId="dd-activity-convs" />
        <KPICard icon={Clock} label="Avg Handle Time" value={handleMin != null ? `${handleMin} min` : '—'} sublabel="per conversation" testId="dd-activity-handle" />
        <KPICard icon={Users} label="Meetings Attended" value={activity.meetingsAttended.toLocaleString()} testId="dd-activity-meetings" />
      </div>

      {/* Phone call block */}
      {hasCalls ? (
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="w-3.5 h-3.5 text-[#171717]" strokeWidth={1.5} />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#171717]">Phone Calls</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPICard icon={Phone} label="Total Calls" value={activity.callsTotal.toLocaleString()} accent="blue" testId="dd-calls-total" />
            <KPICard icon={CheckCircle2} label="Answered" value={activity.callsAnswered.toLocaleString()} accent="emerald" testId="dd-calls-answered" />
            <KPICard icon={XCircle} label="Missed" value={activity.callsMissed.toLocaleString()} accent={activity.callsMissed > 0 ? 'amber' : 'gray'} testId="dd-calls-missed" />
            <KPICard icon={TrendingUp} label="Answer Rate" value={activity.callsAnswerRate != null ? `${activity.callsAnswerRate}%` : '—'} accent={(activity.callsAnswerRate || 0) >= 85 ? 'emerald' : (activity.callsAnswerRate || 0) >= 70 ? 'amber' : 'red'} testId="dd-calls-answer-rate" />
            <KPICard icon={Clock} label="Talk Time" value={talkMin != null ? talkMin >= 60 ? `${(talkMin / 60).toFixed(1)}h` : `${talkMin} min` : '—'} testId="dd-calls-talk" />
            <KPICard icon={Phone} label="In / Out" value={`${(activity.callsInbound || 0)} / ${(activity.callsOutbound || 0)}`} sublabel="answered" testId="dd-calls-inout" />
          </div>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <PlaceholderCard icon={Phone} label="Call Volume" testId="dd-activity-calls" />
          <PlaceholderCard icon={Mail} label="Email Volume" testId="dd-activity-email" />
          <PlaceholderCard icon={Clock} label="Avg Response Time" testId="dd-activity-response" />
        </div>
      )}

      {hasCalls && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <PlaceholderCard icon={Mail} label="Email Volume" testId="dd-activity-email" />
          <PlaceholderCard icon={Clock} label="Avg Response Time" testId="dd-activity-response" />
        </div>
      )}
      <p className="text-[10px] text-[#A3A3A3] mt-3">{activity.placeholderNote}</p>
    </Section>
  );
};

// ── NPS Block ───────────────────────────────────────────────────────────────
export const NPSBlock = ({ nps }) => {
  if (!nps) return null;
  if (nps.totalResponses === 0) {
    return (
      <Section title="Client NPS" icon={Heart} testId="dd-nps">
        <div className="rounded-[10px] border border-dashed border-[#E5E5E5] p-6 text-center bg-[#FAFAFA]">
          <p className="text-sm text-[#737373]">No NPS responses recorded for this scope.</p>
        </div>
      </Section>
    );
  }
  return (
    <Section title="Client NPS" icon={Heart} subtitle={nps.note} testId="dd-nps">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <KPICard icon={Heart} label="Responses" value={nps.totalResponses} testId="dd-nps-total" />
        <KPICard icon={Award} label="Avg Score" value={nps.avgScore?.toFixed(1) || '—'} sublabel="out of 10" testId="dd-nps-avg" />
        <KPICard icon={TrendingUp} label="Rate 7+" value={fmtPct(nps.ratedSevenPlusPct)} accent={(nps.ratedSevenPlusPct || 0) >= 75 ? 'emerald' : 'amber'} testId="dd-nps-rate7" />
        <KPICard icon={Award} label="eNPS" value={nps.enps != null ? `${nps.enps > 0 ? '+' : ''}${nps.enps}` : '—'} accent={(nps.enps || 0) >= 30 ? 'emerald' : (nps.enps || 0) >= 0 ? 'amber' : 'red'} testId="dd-nps-enps" />
        <KPICard icon={AlertCircle} label="Detractors" value={nps.detractors} accent={nps.detractors > 0 ? 'red' : 'gray'} testId="dd-nps-detractors" />
      </div>
      {nps.topComments?.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Recent feedback</p>
          {nps.topComments.slice(0, 6).map((c, idx) => (
            <div key={`${c.planNumber}-${idx}-${c.comment.slice(0, 30)}`} className="bg-white border border-[#E5E5E5] rounded-[8px] p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`px-1.5 py-0.5 rounded-[4px] text-[10px] font-semibold border ${c.category === 'Promoter' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : c.category === 'Detractor' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{c.category} · {c.score}</span>
                <span className="text-[10px] font-mono text-[#A3A3A3]">{c.planNumber}</span>
              </div>
              <p className="text-[12px] text-[#262626] italic leading-relaxed">&ldquo;{c.comment}&rdquo;</p>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ── Everperform Pulse ───────────────────────────────────────────────────────
// Uses the four dimensions Everperform exposes: Wellbeing, Compliance,
// Performance and the Leading-Indicator (Li) score.  Any dimension with a
// missing score falls back to a friendly em-dash rather than a zero.
const scoreBand = (score) => {
  if (score == null) return { bg: 'bg-[#FAFAFA]', text: 'text-[#737373]', border: 'border-[#E5E5E5]' };
  if (score >= 85)   return { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-300' };
  if (score >= 70)   return { bg: 'bg-lime-50',    text: 'text-lime-800',    border: 'border-lime-300' };
  if (score >= 55)   return { bg: 'bg-amber-50',   text: 'text-amber-900',   border: 'border-amber-300' };
  return { bg: 'bg-rose-50', text: 'text-rose-800', border: 'border-rose-300' };
};

const TrendArrow = ({ value }) => {
  if (value == null || Math.abs(value) < 0.5) return <Minus className="w-3 h-3 text-[#A3A3A3]" strokeWidth={2} />;
  return value > 0
    ? <TrendingUp className="w-3 h-3 text-emerald-600" strokeWidth={2} />
    : <TrendingDown className="w-3 h-3 text-rose-600" strokeWidth={2} />;
};

const PulseScoreCard = ({ label, block, testId }) => {
  const band = scoreBand(block?.score);
  return (
    <div
      data-testid={testId}
      className={`rounded-[10px] border p-4 min-h-[110px] flex flex-col justify-between ${band.bg} ${band.border}`}
    >
      <div className="flex items-start justify-between">
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${band.text} opacity-80`}>{label}</p>
        <TrendArrow value={block?.variance} />
      </div>
      <div>
        <p className={`text-3xl font-bold font-heading ${band.text}`}>{block?.score == null ? '—' : Math.round(block.score)}</p>
        <div className="flex items-center gap-2 mt-1 text-[10px] text-[#737373]">
          {block?.variance != null && (
            <span className={block.variance > 0 ? 'text-emerald-700 font-semibold' : block.variance < 0 ? 'text-rose-700 font-semibold' : 'text-[#737373]'}>
              {block.variance > 0 ? '+' : ''}{Math.round(block.variance)} vs prev
            </span>
          )}
          {block?.atRisk != null && block.atRisk > 0 && (
            <span className="text-rose-700 font-semibold">· {block.atRisk} at risk</span>
          )}
        </div>
        {(block?.positiveMovers != null || block?.negativeMovers != null) && (
          <p className="text-[10px] text-[#737373] mt-0.5">
            {block?.positiveMovers ? `↑ ${block.positiveMovers}` : ''}
            {block?.positiveMovers && block?.negativeMovers ? '  ' : ''}
            {block?.negativeMovers ? `↓ ${block.negativeMovers}` : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export const EverperformBlock = ({ everperform, subjectLabel = 'employee' }) => {
  if (!everperform || !everperform.available) {
    return (
      <Section title="Everperform Pulse" icon={TrendingUp} testId="dd-everperform">
        <div className="rounded-[10px] border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-6 text-center">
          <p className="text-sm text-[#737373]">{everperform?.placeholderNote || 'No Everperform pulse for this person yet.'}</p>
          <p className="text-[11px] text-[#A3A3A3] mt-2">Upload the latest custom report on the Data Upload page.</p>
        </div>
      </Section>
    );
  }
  const li = everperform.leadingIndicator || {};
  const coverage = everperform.memberCount
    ? `Team rollup · ${everperform.membersCovered}/${everperform.memberCount} members with pulses`
    : `Rater compliance ${everperform.raterCompliance == null ? '—' : Math.round(everperform.raterCompliance)}%`;
  return (
    <Section
      title="Everperform Pulse"
      icon={TrendingUp}
      testId="dd-everperform"
      subtitle={`Weekly employee pulse — self-reported by ${subjectLabel}. ${coverage}.`}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <PulseScoreCard label="Wellbeing"      block={everperform.wellbeing}   testId="dd-ep-wellbeing" />
        <PulseScoreCard label="Compliance"     block={everperform.compliance}  testId="dd-ep-compliance" />
        <PulseScoreCard label="Performance"    block={everperform.performance} testId="dd-ep-performance" />
        <PulseScoreCard
          label="Leading Indicator"
          block={{
            score: li.curr,
            variance: li.var,
            atRisk: li.riskFactorsCurr,
          }}
          testId="dd-ep-li"
        />
      </div>
      <div className="mt-3 flex items-center gap-4 flex-wrap text-[11px] text-[#525252]">
        <span>
          <span className="font-semibold text-[#171717]">Composite</span>{' '}
          {everperform.compositeScore == null ? '—' : Math.round(everperform.compositeScore)} / 100
          <span className="text-[#A3A3A3]"> · equal weight across Wellbeing, Compliance and Performance</span>
        </span>
        {li.riskFactorsCurr != null && (
          <span className="text-[#737373]">Risk factors: <span className="font-semibold text-[#171717]">{li.riskFactorsCurr}</span>{li.riskFactorsPrev != null ? ` (was ${li.riskFactorsPrev})` : ''}</span>
        )}
      </div>
    </Section>
  );
};

// ── Reusable bits ───────────────────────────────────────────────────────────
const PlaceholderCard = ({ icon: Icon, label, testId }) => (
  <div data-testid={testId} className="bg-white border border-dashed border-[#E5E5E5] rounded-[10px] p-4 flex flex-col items-start justify-between min-h-[110px]">
    <Icon className="w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
    <div>
      <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">{label}</p>
      <p className="text-[12px] text-[#A3A3A3] italic mt-1">Awaiting data feed</p>
    </div>
  </div>
);

export const Section = ({ title, icon: Icon, subtitle, children, testId }) => (
  <section data-testid={testId} className="space-y-3">
    <div>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
        <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-[#737373] mt-1 max-w-3xl leading-relaxed">{subtitle}</p>}
    </div>
    {children}
  </section>
);
